import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from "@mui/material"
import DataTable from "@/components/Tables/DataTable"
import { columns } from "./columns"
import { DateRangePicker } from "rsuite"
import Chart from "@/components/charts/Chart"
import { useEffect, useState } from "react"
import { apiFetch } from "@/hooks/apiFetch"
import "rsuite/dist/rsuite.min.css"
import { exportReport } from "@/hooks/useReportExport"
import { useAppSelector } from "@/lib/hooks"
import { BrandSelect } from "../../Brand/BrandSelect"

// Color palette for categories
const CATEGORY_COLORS = [
  "#3f51b5",
  "#4caf50",
  "#ff9800",
  "#e91e63",
  "#00bcd4",
  "#8bc34a",
  "#ff5722",
  "#9c27b0",
  "#607d8b",
  "#795548",
  "#2196f3",
  "#009688",
  "#ffeb3b",
  "#673ab7",
  "#f44336",
  "#cddc39",
  "#ffc107",
  "#03a9f4",
  "#8d6e63",
  "#9e9e9e",
]

const CategorySalesReport = () => {
  const [categoryData, setCategoryData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [dateRange, setDateRange] = useState([
    new Date(new Date().setDate(new Date().getDate() - 30)), // Last 30 days
    new Date(),
  ])
  const [limit, setLimit] = useState(10)
  const [sortBy, setSortBy] = useState("total_sales")
  const [sortDirection, setSortDirection] = useState("desc")

  // export Daily Report
  const handleExport = async () => {
    return exportReport({
      startDate: dateRange[0].toISOString(),
      endDate: dateRange[1].toISOString(),
      reportType: "category-sales-report",
      limit: limit.toString(),
      sort_by: sortBy,
      sort_direction: sortDirection,
    })
  }

  const selectedBrandId = useAppSelector((state) => state.brand.selectedBrandId)
  useEffect(() => {
    const fetchCategoryData = async () => {
      try {
        setLoading(true)
        setError(null)

        const params = new URLSearchParams({
          start_date: dateRange[0].toISOString(),
          end_date: dateRange[1].toISOString(),
          limit: limit.toString(),
          sort_by: sortBy,
          sort_direction: sortDirection,
          brand_id: selectedBrandId,
        })

        const data = await apiFetch(
          `report/sales-by-category?${params.toString()}`
        )
        setCategoryData(data || [])
      } catch (err) {
        console.error(err)
        setError("Failed to fetch category sales data. Please try again later.")
      } finally {
        setLoading(false)
      }
    }

    fetchCategoryData()
  }, [dateRange, limit, sortBy, sortDirection, selectedBrandId])

  // Prepare chart data with distinct colors
  const chartData =
    categoryData.length > 0
      ? {
          labels: categoryData.map((item) => item.category_name),
          salesData: categoryData.map((item) => parseFloat(item.total_sales)),
          quantityData: categoryData.map((item) =>
            parseInt(item.total_quantity_sold)
          ),
          // Use category_color if available, otherwise cycle through our color palette
          colors: categoryData.map(
            (item, index) =>
              item.category_color ||
              CATEGORY_COLORS[index % CATEGORY_COLORS.length]
          ),
          percentages: categoryData.map((item) =>
            parseFloat(item.sales_percentage || 0)
          ),
        }
      : null

  return (
    <>
      <Box sx={{ p: 3 }}>
        <div className="flex justify-between">
          <Typography variant="h6" gutterBottom>
            <div>Sales by Category Report</div>
          </Typography>
          <div className="w-[300px]">
            <BrandSelect />
          </div>
        </div>

        {/* Filters - unchanged */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <Typography variant="caption" gutterBottom>
                  Date Range
                </Typography>
                <DateRangePicker
                  value={dateRange}
                  onChange={setDateRange}
                  format="MMM dd, yyyy"
                  placeholder="Select date range"
                  cleanable={false}
                  style={{ width: "100%" }}
                  appearance="default"
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Max Categories</InputLabel>
                  <Select
                    value={limit}
                    onChange={(e) => setLimit(e.target.value)}
                    label="Max Categories"
                  >
                    <MenuItem value={5}>Top 5</MenuItem>
                    <MenuItem value={10}>Top 10</MenuItem>
                    <MenuItem value={20}>Top 20</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Sort By</InputLabel>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    label="Sort By"
                  >
                    <MenuItem value="total_sales">Total Sales</MenuItem>
                    <MenuItem value="total_quantity_sold">Units Sold</MenuItem>
                    <MenuItem value="total_products">Product Count</MenuItem>
                    <MenuItem value="total_orders">Order Count</MenuItem>
                    <MenuItem value="avg_order_value">Avg Order Value</MenuItem>
                    <MenuItem value="avg_items_per_order">
                      Avg Items/Order
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Sort Direction</InputLabel>
                  <Select
                    value={sortDirection}
                    onChange={(e) => setSortDirection(e.target.value)}
                    label="Sort Direction"
                  >
                    <MenuItem value="desc">Descending</MenuItem>
                    <MenuItem value="asc">Ascending</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={1}>
                <Button
                  variant="outlined"
                  onClick={() => handleExport()}
                  sx={{
                    textTransform: "capitalize",
                    borderRadius: "7px",
                    fontWeight: "500",
                    fontSize: "13px",
                    padding: "6px 3px",
                    marginTop: "10px",
                  }}
                  color="primary"
                >
                  Export
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Loading and error states - unchanged */}
        {loading && <Typography>Loading...</Typography>}
        {error && <Typography color="error">{error}</Typography>}

        {/* Summary Cards - unchanged */}
        {!loading && !error && categoryData.length > 0 && (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Paper elevation={3} sx={{ p: 2, textAlign: "center" }}>
                <Typography variant="subtitle1">Total Categories</Typography>
                <Typography variant="h5" color="primary">
                  {categoryData.length}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={3} sx={{ p: 2, textAlign: "center" }}>
                <Typography variant="subtitle1">Total Sales</Typography>
                <Typography variant="h5" color="secondary">
                  Rs.{" "}
                  {categoryData
                    .reduce(
                      (sum, item) => sum + parseFloat(item.total_sales),
                      0
                    )
                    .toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={3} sx={{ p: 2, textAlign: "center" }}>
                <Typography variant="subtitle1">Top Category</Typography>
                <Typography>
                  {categoryData[0]?.category_name} (Rs.{" "}
                  {parseFloat(categoryData[0]?.total_sales || 0).toLocaleString(
                    "en-IN",
                    { maximumFractionDigits: 2 }
                  )}
                  )
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Charts with improved color handling */}
        {chartData && (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Sales by Category
                  </Typography>
                  <div className="h-[350px]">
                    <Chart
                      type="pie"
                      data={chartData.salesData}
                      labels={chartData.labels}
                      title="Sales Distribution"
                      backgroundColor={chartData.colors}
                      borderColor={chartData.colors}
                      tooltipLabel="Rs."
                    />
                  </div>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Units Sold by Category
                  </Typography>
                  <Chart
                    type="bar"
                    data={chartData.quantityData}
                    labels={chartData.labels}
                    title="Units Sold"
                    backgroundColor={chartData.colors.map(
                      (color) => `${color}80`
                    )} // Add opacity
                    borderColor={chartData.colors}
                    tooltipLabel="Units"
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Data Table - unchanged */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Category Sales Details
            </Typography>
            <DataTable
              columns={columns}
              data={categoryData}
              isLoading={loading}
              sx={{ mt: 2 }}
            />
          </CardContent>
        </Card>
      </Box>
    </>
  )
}

export default CategorySalesReport
